import { Link, useParams, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import PageSeo from "@/components/feature/PageSeo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, channels } from "@/mocks/news";
import type { Article, Channel } from "@/types/news";

const list = articles as Article[];
const channelList = channels as Channel[];

export default function ChannelPage() {
  const { slug = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const keyword = (searchParams.get("q") ?? "").trim();

  const currentChannel = channelList.find((item) => item.slug === slug);
  const channelName = slug === "all" ? "全部爆料" : currentChannel?.name ?? "未知频道";

  let filtered = slug === "all" ? [...list] : list.filter((item) => item.channelSlug === slug);
  if (keyword) {
    const lower = keyword.toLowerCase();
    filtered = list.filter((item) =>
      [item.title, item.summary, item.channelName, item.author, item.tags.join(" ")]
        .join(" ")
        .toLowerCase()
        .includes(lower)
    );
  }
  filtered.sort((a, b) => b.publishedAt.localeCompare(a.publishedAt));

  const hotSide = [...list].sort((a, b) => b.views - a.views).slice(0, 5);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <PageSeo
        title={`${keyword ? `“${keyword}” 搜索结果` : channelName} | 爆料官网`}
        description={`${channelName}频道汇总爆料官网的最新内容，包含头条新闻、最新动态与今日聚焦信息，按发布时间实时排序。`}
        keywords={`${channelName},爆料官网,今日发布,最新动态,${channelList.map((item) => item.name).join(",")}`}
        canonicalPath={keyword ? "/channel/all" : `/channel/${slug}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${channelName} | 爆料官网`,
            url: `https://byfju.cn/channel/${slug}`,
            inLanguage: "zh-CN",
            description: currentChannel?.description ?? "爆料官网全部内容汇总页。",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://byfju.cn/" },
              {
                "@type": "ListItem",
                position: 2,
                name: channelName,
                item: `https://byfju.cn/channel/${slug}`,
              },
            ],
          },
        ]}
      />
      <SiteHeader />
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: channelName }]} />

      <main className="mx-auto w-full max-w-[1280px] flex-1 px-4 py-8 md:px-6 md:py-10">
        <div className="mb-6 rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {keyword ? `“${keyword}” 的搜索结果` : channelName}
          </h1>
          <p className="mt-2 max-w-3xl font-body text-sm leading-7 text-foreground-600">
            {keyword
              ? "以下内容根据关键词与标题、摘要、标签的匹配程度由新到旧排列。若结果不准确，可尝试更简短的关键词。"
              : currentChannel?.description ?? "汇总爆料官网当前所有频道的发布内容，按时间倒序排列。"}
          </p>
          <p className="mt-4 flex flex-wrap items-center gap-3 font-label text-xs text-secondary-500">
            <span className="flex items-center gap-1">
              <i className="ri-file-list-3-line" aria-hidden="true"></i>
              共 {filtered.length} 篇
            </span>
            <span className="h-3 w-px bg-background-300"></span>
            <span>最后更新 2026-09-10</span>
          </p>
        </div>

        <nav aria-label="频道筛选" className="mb-6 flex flex-wrap gap-2">
          <Link
            to="/channel/all"
            className={`whitespace-nowrap rounded-full border px-4 py-1.5 font-label text-sm transition-colors ${
              slug === "all"
                ? "border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950"
                : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
            }`}
          >
            全部
          </Link>
          {channelList.map((item) => (
            <Link
              key={item.slug}
              to={`/channel/${item.slug}`}
              className={`whitespace-nowrap rounded-full border px-4 py-1.5 font-label text-sm transition-colors ${
                slug === item.slug
                  ? "border-primary-500 bg-primary-500 text-background-50 dark:text-foreground-950"
                  : "border-background-300 text-foreground-700 hover:border-primary-400 hover:text-primary-600"
              }`}
            >
              {item.name}
            </Link>
          ))}
        </nav>

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-secondary-500">
                  <i className="ri-search-eye-line text-2xl" aria-hidden="true"></i>
                </span>
                <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                  暂时没有匹配的内容
                </h2>
                <p className="mt-2 max-w-sm font-body text-sm leading-6 text-foreground-600">
                  换个关键词试试，或者浏览全部频道的发布内容。
                </p>
                <Link
                  to="/channel/all"
                  className="mt-5 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
                >
                  浏览全部爆料
                </Link>
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-background-200">
                {filtered.map((article) => (
                  <div key={article.id} className="py-5 first:pt-0">
                    <ArticleCard article={article} variant="wide" />
                  </div>
                ))}
              </div>
            )}
          </div>

          <aside className="flex flex-col gap-6">
            <section className="rounded-lg border border-background-200 bg-background-50 p-5" aria-labelledby="side-hot">
              <h2 id="side-hot" className="mb-3 flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-primary-500">
                  <i className="ri-fire-line text-lg" aria-hidden="true"></i>
                </span>
                热门推荐
              </h2>
              <div className="flex flex-col">
                {hotSide.map((article) => (
                  <ArticleCard key={article.id} article={article} variant="compact" />
                ))}
              </div>
            </section>

            <section className="rounded-lg border border-background-200 bg-background-100 p-5" aria-labelledby="side-channel">
              <h2 id="side-channel" className="mb-3 flex items-center gap-2 border-b border-background-200 pb-3 font-heading text-base font-bold text-foreground-950">
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className="ri-layout-grid-line text-lg" aria-hidden="true"></i>
                </span>
                频道导航
              </h2>
              <ul className="flex flex-col gap-2">
                {channelList.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/channel/${item.slug}`}
                      className="flex items-center justify-between rounded-md px-3 py-2 font-label text-sm text-foreground-700 transition-colors hover:bg-background-200 hover:text-primary-600"
                    >
                      <span className="flex items-center gap-2">
                        <i className={`${item.icon} text-base text-secondary-500`} aria-hidden="true"></i>
                        {item.name}
                      </span>
                      <i className="ri-arrow-right-s-line text-secondary-400" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          </aside>
        </div>
      </main>

      <SiteFooter />
      <BackToTop />
    </div>
  );
}